import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getPublicConfig } from '../../services/api-client';
import { getApiFileUrl } from '../utils/fileUtils';
import { APP_INFO } from '../constants';

/**
 * The learning centre's own identity, fetched once from /public/config.
 *
 * Two things make this less trivial than it looks:
 *
 *  - It is on the login critical path, and login is unauthenticated, so the
 *    fetch cannot wait for a token.
 *  - Painting the vendor default and then swapping in the centre's logo a
 *    moment later is the exact thing white-labelling is meant to avoid. So the
 *    last known config is mirrored into localStorage and used as the initial
 *    state: a returning visitor paints branded on the first frame, and the
 *    network response only corrects it if something actually changed.
 */

const STORAGE_KEY = 'lms_branding';

const DEFAULTS = {
  centreName: null,
  centreShortName: null,
  logoUrl: null,
  logoDarkUrl: null,
  primaryColor: null,
  loginSubtitle: null,
  websiteUrl: null,
  // Assume self-service reset is off until told otherwise: offering a link that
  // 404s is worse than not offering one, and off is the default deployment.
  selfServiceResetEnabled: false,
  poweredBy: { name: APP_INFO.BRAND, url: 'https://shuhrat.me' },
};

const readCached = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : DEFAULTS;
  } catch {
    return DEFAULTS;
  }
};

const writeCached = (config) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch {
    // A full or disabled storage costs us the first-frame paint, nothing more.
  }
};

const BrandingContext = createContext(null);

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(readCached);
  const [isLoaded, setIsLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const response = await getPublicConfig();
      const next = { ...DEFAULTS, ...(response?.data || {}) };
      setBranding(next);
      writeCached(next);
    } catch {
      // Keep whatever we have. A branding fetch must never block login — the
      // fallback is the vendor logo, which is a cosmetic loss, not an outage.
    } finally {
      setIsLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // The centre's colour reaches CSS as a variable rather than inline styles, so
  // a stylesheet can use it anywhere without every component knowing about it.
  useEffect(() => {
    const root = document.documentElement;
    if (branding.primaryColor) {
      root.style.setProperty('--lms-brand-primary', branding.primaryColor);
    } else {
      root.style.removeProperty('--lms-brand-primary');
    }
  }, [branding.primaryColor]);

  useEffect(() => {
    document.title = branding.centreName || APP_INFO.NAME;
  }, [branding.centreName]);

  const value = useMemo(() => ({ ...branding, isLoaded, refresh }), [branding, isLoaded, refresh]);

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

export const useBranding = () => {
  const context = useContext(BrandingContext);
  // Callers outside the provider (tests, storybook) get the defaults rather
  // than a crash — nothing here is load-bearing enough to throw over.
  return context || { ...DEFAULTS, isLoaded: false, refresh: () => {} };
};

/**
 * The logo to show, given the current theme. Falls back to the light variant
 * when a centre only supplied one, and to the vendor logo when it supplied none.
 */
export const useBrandLogo = (isDark = false) => {
  const { logoUrl, logoDarkUrl } = useBranding();
  const chosen = (isDark && logoDarkUrl) || logoUrl;
  // The server hands back the same /api/download/public/{id} shape as profile
  // photos and question images, so the existing resolver applies.
  return chosen ? getApiFileUrl(chosen) : '/images/lms.png';
};
